import { ModuleTypes } from '$const';
import { ModuleEditOverlay } from '~components/generic/editor/ModuleEditOverlay/ModuleEditOverlay';
import Hint from '~components/generic/elements/Hint/Hint';
import ErrorBoundary from '~components/generic/error-boundary/ErrorBoundary';
import Loader from '~components/generic/loader/Loader';
import { ModulesListPopover } from '~components/generic/module/ModulesListPopover/ModulesListPopover';
import useEditModules from '~components/page/EditmodeHeader/useEditModules';
import { IEntityComponentProps } from '~interfaces/props';
import * as moduleComponents from '~modules/index';
import { toPascalCase } from '~utils/string';
import moduleStyles from '~components/generic/module/moduleStyles.css';

//const Module = forwardRef(function Module(props: IEntityComponentProps, ref: ForwardedRef<HTMLDivElement>) {
const Module = (props: IEntityComponentProps) => {
  const { isEditing: isEditingModules } = useEditModules();

  if (!props.origData) {
    return null;
  }

  const { data, customLoader } = props;
  if (!data) {
    return customLoader ? (
      customLoader
    ) : (
      <div>
        <Loader {...props.loaderProps} />
      </div>
    );
  }

  const rawModuleName = data.type.replace(/^paragraph--/, '');
  const moduleTitle = toPascalCase(rawModuleName);
  const moduleName = 'Module' + moduleTitle;
  const DynamicModule = moduleComponents[moduleName]; // ToDo: Create ModuleFactory to allow correct typings and disable eslint exception `allowComputed`

  if (!DynamicModule) {
    console.group('ModuleNotImplemented');
    console.log({
      rawModuleName,
      moduleTitle,
      moduleName,
    });
    console.groupEnd();
  }

  const {
    canEdit,
    context,
    canMove,
    canDelete,
    isEditing,
    parentContext,
    index,
    siblingsLength,
    isDragging,
    stable,
    entityDef,
  } = props;

  const _canEdit = canEdit && (!context || context === data.id);
  const _canDelete = canDelete !== false;
  const _canMove = canMove || (isEditing && context === parentContext);
  const _canMoveUp = canMove && index > 0;
  const _canMoveDown = canMove && index < siblingsLength;
  const isFullWidth = Boolean(data.attributes.field_full_width);
  const paddingTopType = data.attributes.field_padding_top || '';
  const paddingBottomType = data.attributes.field_padding_bottom || '';
  const className = {
    module: {
      ...props.className,
      [moduleStyles.module]: true,
      [moduleStyles.dragging]: isDragging,
      // [moduleStyles.canMoveModule]: _canMove, // ToDo: disabled for now because we dont want to drag-n-drop modules
      'c-module': true,
      [`c-module--type--${data.type}`]: true,
      [`c-module--index-${index}`]: true,
      ...{
        'u-padding-top-xxl': paddingTopType === 'big',
        'u-padding-top-xl': paddingTopType === '',
        'u-padding-top-md': paddingTopType === 'medium',
        'u-padding-top-sm': paddingTopType === 'small',
        'u-padding-top-none': paddingTopType === 'none',
        'u-padding-bottom-xxl': paddingBottomType === 'big',
        'u-padding-bottom-xl': paddingBottomType === '',
        'u-padding-bottom-md': paddingBottomType === 'medium',
        'u-padding-bottom-sm': paddingBottomType === 'small',
        'u-padding-bottom-none': paddingBottomType === 'none',
      },
    },
    container: {
      container: !isFullWidth,
      relative: true,
      'px-0': true,
      [moduleStyles.canMoveContainer]: _canMove,
    },
  };
  const hasPdfPageBreak = Boolean(data.attributes.field_pdf_page_break);

  if (DynamicModule) {
    return (
      <ErrorBoundary>
        <div className="relative module-container">
          {props.parentType === ModuleTypes.nodePage && isEditingModules && (
            <div className="container relative">
              <ModulesListPopover type={ModuleTypes.nodePage} parentContext={parentContext} index={index} />
            </div>
          )}
          {hasPdfPageBreak && isEditing && <Hint type="pdfPageBreak" />}
          <DynamicModule
            {...props}
            stable={stable}
            canEdit={_canEdit}
            canDelete={_canDelete}
            canMove={_canMove}
            className={className}
            moduleTitle={moduleTitle}
          />
          {isEditing && (_canEdit || _canMove) && (
            <ModuleEditOverlay
              moduleTitle={entityDef.meta.name}
              moduleIcon={entityDef.meta.iconName}
              paddingTopType={paddingTopType}
              paddingBottomType={paddingBottomType}
              isEditing={props.editing || false}
              isProcessing={!stable}
              canEdit={_canEdit}
              canDelete={_canDelete}
              canMoveUp={_canMoveUp}
              canMoveDown={_canMoveDown}
              onStopEdit={props.onStopEdit}
              onStartEdit={props.onStartEdit}
              onSaveEdit={props.onSave}
              onMoveUp={props.onMoveUp}
              onMoveDown={props.onMoveDown}
              onDelete={props.onDelete}
            />
          )}
          {props.parentType === ModuleTypes.nodePage && isEditingModules && index === siblingsLength && (
            <div className="container relative">
              <ModulesListPopover type={ModuleTypes.nodePage} parentContext={parentContext} index={index + 1} />
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  }
  return (
    <div className="u-margin-vertical-lg u-padding-vertical-lg u-border-bottom-grey-lighter u-border-top-grey-lighter">
      <div className="o-container o-container--lg o-text--quiet">
        <span>Module </span>
        <em className="o-text--mono">{moduleName}</em>
        <span>{` isn't implemented yet!`}</span>
      </div>
    </div>
  );
};

export default Module;
